/**
 * QuickLook（ヘッダー検索）のスクラッチ描画エンジン。
 * USF の Instant Search（描画機能）に依存せず検索候補を自前描画する。データは 2 系統:
 *   - 商品                 … Shopify Search & Discovery（Section Rendering / 全言語）
 *   - サジェスト・コレクション … USF instantsearch API（データ供給元として利用・locale は送らない）
 *
 * <quicklook-search> は結果パネルのレンダラに徹し、入力欄/フォームは要素の外に置く
 * （PC=.Header_search.pc .SearchForm / SP=.sp_search 内の .QuicklookSearch_bar）。要素は .sp_search に
 * 1 つだけ出力し、開いた側の外部 input を bindInput で束ねてその入力で結果を駆動する（light DOM）。
 * PC はバー直下の固定ドロップダウン、SP はフルスクリーン オーバーレイ。
 *
 * 設計の要点:
 *   - 入力欄は単一 bind。USF オーバーレイを起こさず IME 変換中にフォーカスが移らない。
 *   - クエリ正規化は API へ投げる瞬間のみ。入力欄の value は書き換えない。
 *   - 取得は部分描画＝フェイルオープン。全滅でもネイティブのフォーム送信で継続。
 *   - DOM 挿入は textContent / createElement のみ（XSS 対策）。
 *   - URL はハードコードせず window.Shopify.routes.root 基点でロケール/マーケット対応。
 */

// USF instantsearch のエンドポイント。ホスト（svc-N=シャード）は usf.settings.searchSvcUrl 経由で別ストアにも
// 追従させる（下記 quicklookUsfEndpoint）。この定数は設定が無いストア向けのフォールバック。
const QUICKLOOK_USF_ENDPOINT_FALLBACK = 'https://svc-3-usf.hotyon.com/instantsearch';

/** USF instantsearch エンドポイント。usf.settings.searchSvcUrl があればそれを基点にし、無ければ既定へフォールバック。 */
function quicklookUsfEndpoint() {
  const base = window.usf && window.usf.settings && window.usf.settings.searchSvcUrl;
  return base ? `${base}instantsearch` : QUICKLOOK_USF_ENDPOINT_FALLBACK;
}

// 各ソースの取得件数と入力デバウンス。
const QUICKLOOK_PRODUCT_LIMIT = 8; // 商品の取得上限（PC は 4 列 × 2 行で並べる想定）
const QUICKLOOK_SUGGEST_LIMIT = 8;
// デバウンスはクエリ長で可変にする。1-2 文字は候補が絞れず打ち直しが多いので長めに待って USF への
// 無駄打ちを抑え、3 文字以上は入力意図が固まるためレスポンス優先で短くする（発火数の抑制と即応の両立）。
const QUICKLOOK_DEBOUNCE_LONG_MS = 600; // 1-2 文字（意図が不確か）
const QUICKLOOK_DEBOUNCE_SHORT_MS = 250; // 3 文字以上（意図が固まる）
const QUICKLOOK_DEBOUNCE_SHORT_MIN_LEN = 3; // この長さ以上で short を適用
const QUICKLOOK_MIN_QUERY_LENGTH = 1;

// 商品はサジェスト（USF）確定後に描画する（先に出すと USF の後追い挿入でレイアウトシフトするため）。
// USF が遅延・ハングしても商品を取り逃さない安全弁の上限待ち時間。
const QUICKLOOK_SUGGEST_GATE_MS = 10000;

// 商品結果を返す Section Rendering セクション（日本語 Predictive 非対応の回避）。
const QUICKLOOK_PRODUCTS_SECTION = 'quicklook-search-results';

// パネル本体（SP バー＋<quicklook-search>＋タレント辞書）を返す Section Rendering セクション。
// 初期 DOM から重い入力前パネルを外し、検索を開いた時に .sp_search へオンデマンド挿入する。
const QUICKLOOK_PANEL_SECTION = 'header-search-panel';

// 閲覧履歴（「最近見た商品」）。検索履歴 searchHistory とは別物。入力前パネル（#prtcRecentlyViewedProducts）に
// 描画する。TOP 等の「チェックした商品」(#check_swiper) は checked-products.js が描画（localStorage のみ共有）。
const QUICKLOOK_RECENT_KEY = 'recentlyViewedProduct';
const QUICKLOOK_RECENT_MAX = 8;

// 検索履歴。localStorage に最近の検索語を新しい順で保持する。
const QUICKLOOK_HISTORY_KEY = 'searchHistory';
const QUICKLOOK_HISTORY_MAX = 10;
// 入力前パネルに表示する最近の検索の最大件数（保持件数 QUICKLOOK_HISTORY_MAX とは別）。
const QUICKLOOK_HISTORY_DISPLAY_MAX = 5;
// PC ドロップダウンの希望幅と viewport 端からの最小マージン（実幅は viewport 内にクランプ）。
const QUICKLOOK_PC_DROPDOWN_WIDTH = 550;
const QUICKLOOK_PC_DROPDOWN_MARGIN = 12;
// 検索ボックス下端とドロップダウン本体の隙間（尻尾の高さぶん）。
const QUICKLOOK_PC_DROPDOWN_GAP = 10;

// 英語表示時にサジェストから除外する日本語文字（かな・漢字・CJK記号・全角/半角フォーム）。
// USF サジェストは日本語でのみ生成されるため、英語表示では日本語名候補を落としアルファベット系だけ残す。
const QUICKLOOK_JP_CHAR = /[　-ヿ㐀-鿿豈-﫿＀-￯]/;

// 直行カードの文字色の出し分け。テーマ色背景が白に近い（相対輝度がこの閾値以上）と白文字が埋もれるため、
// 文字色を黒色に切り替えて視認性を確保する。
const QUICKLOOK_DIRECT_LIGHT_LUMA = 200;
const QUICKLOOK_DIRECT_LIGHT_TEXT_COLOR = '#111';

// SP フルスクリーンオーバーレイ専用のスクロールロック。共通の bodyFixedOn/Off とは分離し、position: fixed で
// body を固定してスクロール位置を退避・復元する（iOS Safari で背面がスクロールへ抜けるのを防ぎ、閉じたとき
// 元のスクロール位置へ確実に戻す）。PC ドロップダウンは背面ロックしないためこの関数は使わない。
let quicklookSpSavedScrollY = 0;
function quicklookLockBodyForSp() {
  quicklookSpSavedScrollY = window.scrollY;
  const style = document.body.style;
  style.position = 'fixed';
  style.top = `-${quicklookSpSavedScrollY}px`;
  style.left = '0';
  style.right = '0';
  style.width = '100%';
}
function quicklookUnlockBodyForSp() {
  const style = document.body.style;
  style.position = '';
  style.top = '';
  style.left = '';
  style.right = '';
  style.width = '';
  // :root の scroll-behavior: smooth を打ち消して即時復元する（省略時はアニメーションで戻ってしまう）。
  window.scrollTo({ top: quicklookSpSavedScrollY, left: 0, behavior: 'instant' });
}

/** Shopify のルート（ロケール/マーケットのプレフィックス付き）。未定義時は '/'。URL 生成の基点にする。 */
function quicklookShopifyRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

/** URL パスから collections/{handle} の handle を取り出す（ロケール接頭辞に依存しない）。取れなければ ''。 */
function quicklookCollectionHandleFromUrl(url) {
  const matched = (url || '').match(/\/collections\/([^/?#]+)/);
  return matched ? matched[1] : '';
}

/** URL パスから products/{handle} の handle を取り出す。取れなければ ''。 */
function quicklookProductHandleFromUrl(url) {
  const matched = (url || '').match(/\/products\/([^/?#]+)/);
  return matched ? matched[1] : '';
}

/**
 * 検索 API へ渡すためのクエリ正規化（入力欄は触らない・コピーのみ）。
 * 全角スペース→半角・連続スペース畳み込み・前後トリム。
 * 戻り値 { query } の query は API 1 本に渡す文字列。
 */
function quicklookNormalizeQuery(raw) {
  const normalized = (raw || '')
    .replace(/　/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return { query: normalized };
}

/** '#rrggbb' / '#rgb' を {r,g,b}(0-255) に。パースできなければ null。 */
function quicklookHexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

/** {r,g,b}(0-255) → {h(0-360), s(0-1), l(0-1)}（色相ずらしで同系色グラデを作るため）。 */
function quicklookRgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

/**
 * 直行カードの背景と文字色を決める。背景はテーマ色を起点に、同色相で少し暗くした色（L−6%）を終点にした
 * 135deg グラデーション。テーマ色が白に近い（相対輝度が閾値以上）ときは白文字が埋もれるため文字色を
 * 黒に切り替える（それ以外は color=null＝CSS 既定の白に委ねる）。
 * パース不能なら null（呼び出し側は CSS 既定に委ねる）。
 * @returns {{background: string, color: (string|null)}|null}
 */
function quicklookDirectStyle(hex) {
  const rgb = quicklookHexToRgb(hex);
  if (!rgb) return null;
  const luma = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
  const { h, s, l } = quicklookRgbToHsl(rgb);
  const dark = `hsl(${Math.round(h)}, ${Math.round(s * 100)}%, ${Math.round(Math.max(0, l - 0.06) * 100)}%)`;
  const background = `linear-gradient(135deg, ${dark}, ${hex})`;
  const color = luma >= QUICKLOOK_DIRECT_LIGHT_LUMA ? QUICKLOOK_DIRECT_LIGHT_TEXT_COLOR : null;
  return { background, color };
}

class QuicklookSearch extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.lastQuery = '';

    // 検索の起点（'quicklook'=入力 / 'history'=検索履歴チップ）。GA4 の search_submitted の source に反映する。
    // search() のたび先頭で読み取り直後に 'quicklook' へ戻す（1 回の検索にだけ効かせる）。
    this.searchOrigin = 'quicklook';

    this.onInput = this.onInput.bind(this);
    this.onCompositionEnd = this.onCompositionEnd.bind(this);
    this.onSpFocus = this.onSpFocus.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onPrePanelClick = this.onPrePanelClick.bind(this);

    // 現在 bind 中の外部 input/form（PC=.SearchForm / SP=.QuicklookSearch_bar）。生成時には解決しない。
    this.input = null;
    this.form = null;
    // 所属する .sp_search パネル（表示クラス・位置・スクロール状態の付け外し先）。connectedCallback で解決。
    this.panel = null;
    // 表示形態の実行時モード。null=未起動 / 'sp'=フルスクリーン / 'pc'=バー直下ドロップダウン。
    this.mode = null;
    this.pcTrigger = null;
    this.onPcDocPointer = this.onPcDocPointer.bind(this);
    this.onPcKeydown = this.onPcKeydown.bind(this);
    this.onPcReposition = this.onPcReposition.bind(this);

    // クエリ長で待ち時間を変える可変デバウンスのタイマー（scheduleSearch が張り替える）。
    this.searchTimer = null;

    // 商品検索（S&D）が 0 件と判明した最新の検索語（正規化済み）。この語は検索履歴に保存しない
    this.zeroHitQuery = null;
  }

  connectedCallback() {
    this.preEl = this.querySelector('.QuicklookSearch_pre');
    this.resultsEl = this.querySelector('.QuicklookSearch_results');
    this.suggestsEl = this.querySelector('.QuicklookSearch_suggests');
    this.productsEl = this.querySelector('.QuicklookSearch_products');

    if (!this.resultsEl) return;

    // 所属する .sp_search パネルは挿入後に不変なので 1 度だけ解決してキャッシュする。
    this.panel = this.closest('.sp_search');

    // 表示言語（ja / en）。USF へ locale は送らず、英語サジェストの日本語除外・直行 URL の /en 接頭辞・
    // タレント表示名（name_en / name_ja）の出し分けにのみ使う。
    this.locale = (this.dataset.locale || 'ja').toLowerCase().startsWith('en') ? 'en' : 'ja';
    // 未設定なら空＝USF 取得はフェイルオープン。
    this.usfApiKey = (window.usf && window.usf.settings && window.usf.settings.siteId) || '';
    this.talentDirectLabel = this.dataset.labelTalentCollection || '{name}';

    this.historyEl = this.querySelector('.QuicklookSearch_history');
    this.historyListEl = this.querySelector('.QuicklookSearch_historyList');
    this.historyClearAllEl = this.querySelector('.QuicklookSearch_historyClearAll');

    // SP の検索バー（要素の外・.sp_search 内の兄弟）。入力欄/フォーム/クリア×/キャンセルはここにある。
    // PC は .Header_search.pc .SearchForm を入力欄に使う（activatePc で bind）。
    this.spBar = this.panel ? this.panel.querySelector('.QuicklookSearch_bar') : null;
    this.spInput = this.spBar ? this.spBar.querySelector('input[name="q"]') : null;
    this.spForm = this.spBar ? this.spBar.querySelector('form') : null;
    // クリア×／キャンセルは SP バー側にのみ存在（PC は .SearchForm のみ）。
    this.clearBtn = this.spBar ? this.spBar.querySelector('.QuicklookSearch_clear') : null;
    this.cancelBtn = this.spBar ? this.spBar.querySelector('.QuicklookSearch_cancel') : null;

    if (this.historyClearAllEl) {
      this.historyClearAllEl.addEventListener('click', () => this.clearAllHistory());
    }
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        if (!this.input) return;
        this.input.value = '';
        this.toggleClearButton();
        this.search('');
        this.input.focus();
      });
    }
    if (this.cancelBtn) {
      // 「検索を閉じる」クリック時だけ close を計測する（外側クリック/Esc/遷移では発火させない）。
      this.cancelBtn.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('quicklook:close'));
        this.closeOverlay();
      });
    }

    // 入力前パネルの導線（注目タレント / 閲覧履歴 / ホロカ常設）のクリックを計測イベントへ委譲する。
    if (this.preEl) this.preEl.addEventListener('click', this.onPrePanelClick);

    // SP 入力欄のフォーカスで SP モードを起動（bind ＋ パネル表示）。PC は別経路（activatePc）。
    if (this.spInput) this.spInput.addEventListener('focus', this.onSpFocus);

    // SP を既定 bind しておく（アイコンのトグルで開くため open 経路の有無に関わらず入力を効かせる）。
    if (this.spInput) this.bindInput(this.spInput, this.spForm);

    // 検索結果ページ（/search?q=）で SP 入力欄に検索語を復元する。SP パネルは Section Rendering で
    // ルート（/）基点に取得するためサーバー側に search オブジェクトが無く value="{{ search.terms }}"
    // が空になる（PC は初期 DOM 描画なので復元される）。URL の q を読んで PC との差を埋める。
    this.restoreSpSearchTerms();

    // 初期は入力前パネル表示。可視/位置は open 経路（SP=show / PC=activatePc）が制御。
    this.showPrePanel();
  }

  /**
   * SP 入力欄に検索結果ページの検索語を復元する。/search のときだけ URL クエリ q を反映し、
   * 既に値がある場合（サーバー描画フォールバックで search.terms が入るケース）は上書きしない。
   */
  restoreSpSearchTerms() {
    if (!this.spInput || this.spInput.value) return;
    if (!/\/search\/?$/.test(location.pathname)) return;
    const terms = new URLSearchParams(location.search).get('q');
    if (terms) this.spInput.value = terms;
  }

  /** 外部 input（PC=.SearchForm / SP=.QuicklookSearch_bar）を bind し直す。前回 bind のリスナーを外す。 */
  bindInput(inputEl, formEl) {
    if (this.input === inputEl) {
      this.form = formEl || this.form;
      return;
    }
    if (this.input) {
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('compositionend', this.onCompositionEnd);
    }
    if (this.form) this.form.removeEventListener('submit', this.onFormSubmit);

    this.input = inputEl || null;
    this.form = formEl || (inputEl ? inputEl.closest('form') : null);

    if (this.input) {
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('compositionend', this.onCompositionEnd);
    }
    if (this.form) this.form.addEventListener('submit', this.onFormSubmit);
  }

  /**
   * SP モードを起動する（bind ＋ 表示切替）。restore=true かつ入力欄に前回語が残っていれば結果を復元する。
   * アイコンで開く経路（openSp）は restore を渡さず常に入力前パネル＝開いた瞬間には結果を出さない。
   * ユーザーが入力欄をフォーカスした場合（onSpFocus）だけ復元する。value は DOM 依存なのでリロードで消える。
   */
  activateSp(restore = false) {
    if (!this.spInput) return;
    this.mode = 'sp';
    this.bindInput(this.spInput, this.spForm);
    this.toggleClearButton();
    if (restore && this.spInput.value.trim()) {
      this.search(this.spInput.value);
    } else {
      this.showPrePanel();
    }
  }

  /** SP 入力欄への手動フォーカスで SP モードを起動する。ユーザー操作なので前回語があれば結果を復元する。 */
  onSpFocus() {
    this.activateSp(true);
  }

  onFormSubmit() {
    // ネイティブの Enter / アイコン送信で検索語を履歴に保存してから遷移する（同期 setItem）。
    this.saveSearchHistory(this.input.value);
  }

  /**
   * クエリ長に応じたデバウンスで search をスケジュールする。正規化後の文字数が
   * QUICKLOOK_DEBOUNCE_SHORT_MIN_LEN 以上なら short（即応）、未満なら long（無駄打ち抑制）。
   * 連続入力では前回のタイマーを張り替える（＝デバウンス）。
   */
  scheduleSearch(value) {
    const { query } = quicklookNormalizeQuery(value);
    const wait =
      query.length >= QUICKLOOK_DEBOUNCE_SHORT_MIN_LEN ? QUICKLOOK_DEBOUNCE_SHORT_MS : QUICKLOOK_DEBOUNCE_LONG_MS;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.search(value), wait);
  }

  onCompositionEnd() {
    // 変換確定の保険。多くのブラウザは確定後にも input を発火するが、発火しない IME もあるため
    // 確定値で必ず一度検索する（onInput と重複しても debounce と lastQuery ガードで二重取得はしない）。
    this.scheduleSearch(this.input.value);
  }

  onInput() {
    this.toggleClearButton();
    // IME 変換中（未確定）でも入力値でそのまま検索する。ひらがな入力の途中からサジェストを出すため。
    this.scheduleSearch(this.input.value);
  }

  toggleClearButton() {
    if (this.clearBtn) this.clearBtn.hidden = !this.input || this.input.value.length === 0;
  }

  /**
   * SP オーバーレイを閉じる（キャンセル）。.sp_search の表示クラスとスクロールロックを戻し結果表示を入力前に戻す。
   * 入力値は消さず保持する（次に入力欄をフォーカスしたとき onSpFocus が残った値で結果を復元する）。明示クリアはクリア×ボタンで行う。
   */
  closeOverlay() {
    if (this.panel) this.panel.classList.remove('show');
    quicklookUnlockBodyForSp();
    this.mode = null;
    // 結果表示だけ畳んで入力前パネルに戻す。lastQuery もリセットされ、再フォーカス時に同じ語でも再検索できる。
    this.search('');
  }

  // ---- PC ドロップダウンモード（外部 .SearchForm 入力で駆動） ---------------
  // PC は .Header_search.pc .SearchForm の入力欄がそのまま検索入力。フォーカス時にこの単一パネルを
  // .SearchForm 直下の固定ドロップダウンとして開き、その input を bind して結果を描画する。

  /**
   * PC: .SearchForm の入力欄を bind し、.sp_search をバー直下の固定ドロップダウンとして開く。
   * trigger（.SearchForm）の getBoundingClientRect() を測って位置を inline 指定する。
   */
  activatePc(trigger) {
    if (!this.panel || !trigger) return;
    const pcInput = trigger.querySelector('input[name="q"]');
    if (!pcInput) return;
    const pcForm = trigger.querySelector('form');
    this.pcTrigger = trigger;
    this.bindInput(pcInput, pcForm);

    if (this.mode !== 'pc') {
      this.mode = 'pc';
      this.panel.classList.add('is-pc-dropdown', 'show');
      document.dispatchEvent(new CustomEvent('quicklook:open', { detail: { trigger: 'focus' } }));
      // 閉じる契機：ドロップダウン外のポインタ操作・Esc。リサイズ時のみ位置を測り直す。
      // ヘッダーは position:fixed でスクロールしても動かないため、スクロール追従は不要（トリガーの
      // viewport 位置が変わらず、追従しても同値を書き戻すだけ）。
      document.addEventListener('pointerdown', this.onPcDocPointer, true);
      document.addEventListener('keydown', this.onPcKeydown);
      window.addEventListener('resize', this.onPcReposition);
    }
    this.positionPcDropdown();

    this.toggleClearButton();
    if (pcInput.value.trim()) {
      this.search(pcInput.value);
    } else {
      this.showPrePanel();
    }
  }

  /**
   * trigger（.SearchForm）の現在位置に合わせ、バー直下に上向き吹き出しとして配置する。
   * 幅は QUICKLOOK_PC_DROPDOWN_WIDTH まで広げて検索フォーム中央にそろえ、viewport 内にクランプする。
   * 吹き出しの尻尾（CSS の ::after）は検索ボックス中央に合わせ、その位置を --ql-arrow-left で渡す。
   */
  positionPcDropdown() {
    if (!this.panel || !this.pcTrigger) return;
    const rect = this.pcTrigger.getBoundingClientRect();
    const margin = QUICKLOOK_PC_DROPDOWN_MARGIN;

    const width = Math.min(QUICKLOOK_PC_DROPDOWN_WIDTH, window.innerWidth - margin * 2);
    // 検索フォームの中央にドロップダウンの中央をそろえる。
    let left = rect.left + rect.width / 2 - width / 2;
    if (left + width > window.innerWidth - margin) left = window.innerWidth - margin - width;
    if (left < margin) left = margin;

    this.panel.style.top = `${Math.round(rect.bottom + QUICKLOOK_PC_DROPDOWN_GAP)}px`;
    this.panel.style.left = `${Math.round(left)}px`;
    this.panel.style.width = `${Math.round(width)}px`;

    // 尻尾は検索ボックスの中心に合わせる（パネル左基準）。角丸内に収まるようクランプ。
    const arrowCenter = rect.left + rect.width / 2;
    const arrowLeft = Math.max(16, Math.min(width - 16, arrowCenter - left));
    this.panel.style.setProperty('--ql-arrow-left', `${Math.round(arrowLeft)}px`);
  }

  onPcReposition() {
    if (this.mode === 'pc') this.positionPcDropdown();
  }

  /** ドロップダウン外（パネルでもトリガーでもない場所）のポインタ操作で閉じる。 */
  onPcDocPointer(event) {
    if (this.mode !== 'pc') return;
    if (this.panel && this.panel.contains(event.target)) return;
    if (this.pcTrigger && this.pcTrigger.contains(event.target)) return;
    this.closePcDropdown();
  }

  onPcKeydown(event) {
    if (event.key === 'Escape' && this.mode === 'pc') this.closePcDropdown();
  }

  /** PC ドロップダウンを閉じ、inline 位置指定と監視リスナーを解除する。 */
  closePcDropdown() {
    if (this.mode !== 'pc') return;
    // 外側クリック/Esc で閉じたときも close を計測する（open と対にし、確定待ちの検索も破棄させる）。
    document.dispatchEvent(new CustomEvent('quicklook:close'));
    this.mode = null;
    if (this.panel) {
      this.panel.classList.remove('is-pc-dropdown', 'show');
      this.panel.style.top = '';
      this.panel.style.left = '';
      this.panel.style.width = '';
      this.panel.style.removeProperty('--ql-arrow-left');
    }
    document.removeEventListener('pointerdown', this.onPcDocPointer, true);
    document.removeEventListener('keydown', this.onPcKeydown);
    window.removeEventListener('resize', this.onPcReposition);
    if (this.input) this.input.blur();
  }

  showPrePanel() {
    this.renderSearchHistory();
    if (this.preEl) this.preEl.hidden = false;
    this.resultsEl.hidden = true;
    // 入力前ビューは縦が短くスクロール不要。状態クラスを外し、登場アニメの下スライドで縦バーを出さない。
    if (this.panel) this.panel.classList.remove('has-results');
  }

  /**
   * 入力前パネルの導線クリックを計測用カスタムイベントへ委譲する（リンク遷移は妨げない）。
   *   - 最近チェックしたタレント → quicklook:recent-talent-click { handle, position }
   *   - 注目の新作タレント → quicklook:featured-click { handle, position }
   *   - 閲覧履歴（最近見た商品） → quicklook:recent-click { handle, position }
   *   - ホロカ常設導線 → quicklook:cardgame-click
   */
  onPrePanelClick(event) {
    const talent = event.target.closest('.QuicklookSearch_talent');
    if (talent) {
      const list = talent.closest('.QuicklookSearch_talents');
      const position = list
        ? Array.from(list.querySelectorAll('.QuicklookSearch_talent')).indexOf(talent) + 1
        : null;
      const handle = quicklookCollectionHandleFromUrl(talent.getAttribute('href'));
      // 最近チェックしたタレント（S8）と注目の新作タレント（S7）は同じマークアップ。差し込み先コンテナで区別する。
      if (list && list.hasAttribute('data-recent-talents')) {
        document.dispatchEvent(
          new CustomEvent('quicklook:recent-talent-click', { detail: { handle, position } })
        );
        return;
      }
      document.dispatchEvent(
        new CustomEvent('quicklook:featured-click', {
          detail: { handle, position },
        })
      );
      return;
    }

    const recent = event.target.closest('.recent_view_wrap a[href]');
    if (recent) {
      const wrap = recent.closest('.recent_view_wrap');
      const position = wrap ? Array.from(wrap.querySelectorAll('a[href]')).indexOf(recent) + 1 : null;
      document.dispatchEvent(
        new CustomEvent('quicklook:recent-click', {
          detail: { handle: quicklookProductHandleFromUrl(recent.getAttribute('href')), position },
        })
      );
      return;
    }

    if (event.target.closest('.QuicklookSearch_cardgame')) {
      document.dispatchEvent(new CustomEvent('quicklook:cardgame-click', { detail: {} }));
    }
  }

  // ---- 検索履歴 ----------------------------------------------------------
  // localStorage 'searchHistory' に最近の検索語を新しい順で保持。端末・ブラウザ間では非共有。

  loadHistory() {
    try {
      const stored = JSON.parse(localStorage.getItem(QUICKLOOK_HISTORY_KEY));
      return Array.isArray(stored) ? stored.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  saveSearchHistory(rawTerm) {
    const term = quicklookNormalizeQuery(rawTerm).query;
    if (!term) return;
    // QuickLook の商品検索で 0 件と確定している語は保存しない（0 件の語が履歴に溜まるのを防ぐ）。
    if (this.zeroHitQuery && term.toLowerCase() === this.zeroHitQuery.toLowerCase()) return;
    // 重複は先頭へ繰り上げ（既存を除いてから unshift）。上限 10 件。
    const next = [term, ...this.loadHistory().filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(
      0,
      QUICKLOOK_HISTORY_MAX
    );
    try {
      localStorage.setItem(QUICKLOOK_HISTORY_KEY, JSON.stringify(next));
    } catch (e) {
      /* localStorage 不可（プライベートモード等）はサイレントに無視 */
    }
  }

  deleteSearchHistory(term) {
    const next = this.loadHistory().filter((t) => t !== term);
    try {
      localStorage.setItem(QUICKLOOK_HISTORY_KEY, JSON.stringify(next));
    } catch (e) {
      /* noop */
    }
    this.renderSearchHistory();
  }

  clearAllHistory() {
    try {
      localStorage.removeItem(QUICKLOOK_HISTORY_KEY);
    } catch (e) {
      /* noop */
    }
    this.renderSearchHistory();
  }

  /** 入力前パネルに履歴チップを新しい順で描画（XSS セーフ。#id でなく要素スコープのクラス参照）。 */
  renderSearchHistory() {
    if (!this.historyEl || !this.historyListEl) return;
    const items = this.loadHistory();
    this.historyListEl.replaceChildren();
    if (!items.length) {
      this.historyEl.hidden = true;
      return;
    }
    this.historyEl.hidden = false;

    items.slice(0, QUICKLOOK_HISTORY_DISPLAY_MAX).forEach((term) => {
      // チップは枠だけのラッパー（div）。中に「語で検索」と「削除」を独立した button として並置する
      // （button の入れ子は不正なため分離）。
      const chip = document.createElement('div');
      chip.className = 'QuicklookSearch_chip';

      // 本文ボタン: 即送信せず、入力欄に値を入れてその場で検索する（直行/候補まで出す）。
      const text = document.createElement('button');
      text.type = 'button';
      text.className = 'QuicklookSearch_chipText';
      text.textContent = term;
      text.addEventListener('click', () => {
        // 起点を history にしてから検索（source=history で計測）。focus は search の後に呼び、
        // フォーカス起点の同一語再検索を lastQuery ガードで空振りさせ二重計測を避ける。
        this.searchOrigin = 'history';
        this.input.value = term;
        this.toggleClearButton();
        this.search(term);
        this.input.focus();
      });

      // 削除ボタン: この語だけ履歴から消す。ラベルに語を含め聞き分けられるようにする。
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'QuicklookSearch_chipRemove';
      remove.setAttribute('aria-label', `${this.dataset.labelHistoryDelete || 'delete'}: ${term}`);
      remove.textContent = '×';
      remove.addEventListener('click', () => this.deleteSearchHistory(term));

      chip.appendChild(text);
      chip.appendChild(remove);
      this.historyListEl.appendChild(chip);
    });
  }

  showResultsPanel() {
    if (this.preEl) this.preEl.hidden = true;
    this.resultsEl.hidden = false;
    // 結果ビューは件数に応じて縦に伸びるため、スクロール許可の状態クラスを付ける（CSS が overflow を出し分ける）。
    if (this.panel) this.panel.classList.add('has-results');
  }

  /**
   * 入力 → 商品（Section Rendering）＋サジェスト/コレクション（USF）を並列取得 → 取れた枠から部分描画。
   * サジェストは返り次第即描画し先行表示、商品は上段サジェスト確定後に描画してレイアウトシフトを防ぐ
   * （安全弁: USF 遅延時は QUICKLOOK_SUGGEST_GATE_MS で打ち切り）。前回リクエストは abort する。
   */
  search(rawValue) {
    // デバウンス予約済みの検索を破棄する。クリア×・履歴チップ・閉じる操作などの直接呼び出しで
    // 予約が残っていると、後から古いクエリの遅延検索が走ってしまう。
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    // この検索の起点を確定し、フラグは即リセットする（履歴チップ由来は 1 回だけ source=history にする）。
    const origin = this.searchOrigin || 'quicklook';
    this.searchOrigin = 'quicklook';

    const { query } = quicklookNormalizeQuery(rawValue);

    if (query.length < QUICKLOOK_MIN_QUERY_LENGTH) {
      // 進行中の取得を中断する。中断しないと放棄したクエリのレスポンスが後着で描画・計測されてしまう。
      if (this.abortController) this.abortController.abort();
      this.lastQuery = '';
      this.clearResults();
      this.showPrePanel();
      return;
    }
    if (query === this.lastQuery) return;
    this.lastQuery = query;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.showResultsPanel();
    this.clearResults();
    // 商品は後追い取得（サジェスト確定後に描画）。取得完了までは商品枠に回転スピナーを出す。
    if (this.productsEl) this.productsEl.classList.add('is-loading');

    // 商品は Section Rendering（S&D フル検索・価格/Global-e 整合）、サジェスト語・コレクションは USF。
    // どちらも取得元は日英共通（詳細は fetchProductsSection / fetchUsf のコメント参照）。
    const productsPromise = this.fetchProductsSection(query, signal);
    const suggestPromise = this.fetchUsf(query, signal);

    // サジェスト（USF）: 返り次第即描画。USF が失敗してもフェイルオープンで空ソース（サジェスト/
    // コレクションなし）を渡し、サジェスト枠は空のまま確定させる（商品と「すべて見る」は別経路で描画）。
    suggestPromise
      .then(
        (source) => source,
        () => ({ suggestions: [], collections: [] })
      )
      .then((source) => {
        if (signal.aborted) return;
        this.renderSuggests(query, source);
      });

    // 商品は USF 確定（成功/失敗どちらでも）を待ってから描画する（先に出すと押し下げでレイアウトシフト）。
    // 安全弁として最大 QUICKLOOK_SUGGEST_GATE_MS 待ち、USF 遅延時も商品を取り逃さない。
    const suggestSettled = suggestPromise.then(
      () => {},
      () => {}
    );
    const suggestGate = new Promise((resolve) => {
      const timer = setTimeout(resolve, QUICKLOOK_SUGGEST_GATE_MS);
      signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
    });

    Promise.race([suggestSettled, suggestGate])
      .then(() => Promise.allSettled([productsPromise]))
      .then(([productsRes]) => {
        if (signal.aborted) return;

        // S&D がネットワークエラー等で失敗したときは「該当なし」を誤表示せず商品枠の中身を出さない
        // （成功側＝サジェストだけ残す）。フェイルオープンの「すべて見る」導線だけは描画する。
        const failed = productsRes.status === 'rejected';
        const productNodes = failed ? [] : productsRes.value.productNodes;
        // GA4 の result_count には表示上限（8 件）ではなく S&D の総ヒット件数を送る。
        const resultCount = failed ? 0 : productsRes.value.resultCount;
        // 取得失敗時は lastQuery を解除し、同じ語の再入力で再取得できるようにする（成功するまで確定させない）。
        if (failed) this.lastQuery = '';
        // 0 件と確定した語を控える（saveSearchHistory が履歴保存をスキップする判定に使う）。
        if (!failed) this.zeroHitQuery = resultCount === 0 ? query : null;
        this.renderProducts(productNodes, failed, query, resultCount);

        // 検索確定を計測へ通知（GA4 search_submitted の発火源）。source は起点、
        // failed は S&D 取得失敗（フォールバック率の観測用。prtc 側で source=quicklook_error に振り替える）。
        document.dispatchEvent(
          new CustomEvent('quicklook:rendered', {
            detail: { query, productCount: resultCount, locale: this.locale, source: origin, failed },
          })
        );
      });
  }

  // ---- データ取得 --------------------------------------------------------

  /**
   * 商品結果を Section Rendering で取得する（全言語）。ネイティブ /search（S&D 適用）をサーバー描画する
   * `quicklook-search-results` セクションを fetch し、商品カード（.QuicklookSearch_card）ノードを抽出して返す。
   * 日本語 Predictive 非対応を回避し、価格は多通貨（Global-e）整合のサーバー描画になる。
   */
  fetchProductsSection(query, signal) {
    const root = quicklookShopifyRoot();
    const url = `${root}search?q=${encodeURIComponent(query)}&section_id=${QUICKLOOK_PRODUCTS_SECTION}&type=product`;
    return fetch(url, { signal })
      .then((res) => {
        if (!res.ok) throw new Error(`section ${res.status}`);
        return res.text();
      })
      .then((html) => {
        // フラグメント（セクションの div ラッパー）なので template で軽量パースする（フル Document は作らない）。
        const template = document.createElement('template');
        template.innerHTML = html;
        const section =
          template.content.getElementById(`shopify-section-${QUICKLOOK_PRODUCTS_SECTION}`) || template.content;
        // 総ヒット件数はセクションが data-result-count（search.results_count）で出力する。表示は上限で間引く。
        const row = section.querySelector('.QuicklookSearch_prodrow');
        const parsedCount = row ? parseInt(row.dataset.resultCount, 10) : NaN;
        const resultCount = Number.isFinite(parsedCount) ? parsedCount : 0;
        const cards = Array.from(section.querySelectorAll('.QuicklookSearch_card')).slice(0, QUICKLOOK_PRODUCT_LIMIT);
        // 別ドキュメント由来のノードは importNode で取り込む。
        return { productNodes: cards.map((node) => document.importNode(node, true)), resultCount };
      });
  }

  /**
   * USF instantsearch をデータ API として叩く（サジェスト語・コレクション）。描画機能は使わない。
   * locale は送らない: コレクションは locale 非依存で英語ローマ字でも日本語名タレントに一致し、
   * サジェストは日本語でのみ生成される（locale=en は null・最大 6 件固定でオーバーフェッチ不可）ため、
   * 日本語サジェストを取得して英語表示時のみ後段で除外する。429 時はフェイルオープンに任せる。
   * 商品は Section Rendering で取得するため take=0 で USF からは商品を返させない（payload 削減）。
   */
  fetchUsf(query, signal) {
    const params = new URLSearchParams({
      q: query,
      apiKey: this.usfApiKey,
      take: '0',
      showCollections: '1',
      showPages: '0',
    });

    return fetch(`${quicklookUsfEndpoint()}?${params.toString()}`, { signal })
      .then((res) => {
        if (!res.ok) throw new Error(`usf ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const data = (json && json.data) || {};
        // コレクションは件数で切らない。描画側がタレント辞書で絞ってから 1 件に確定させるため、
        // ここで上限を掛けると関連度順で後ろに来たタレントコレクションを取り逃す。
        const collections = (data.collections || [])
          .map((c) => ({ title: c.title || c.urlName || '', handle: c.urlName || '' }))
          .filter((c) => c.handle);
        const rawSuggestions = (data.suggestions || []).slice(0, QUICKLOOK_SUGGEST_LIMIT).filter(Boolean);
        // 英語表示は日本語混在のサジェストを除外しアルファベット系のみ残す（除外後 0 件なら collections のみ）。
        const suggestions =
          this.locale === 'en' ? rawSuggestions.filter((word) => !QUICKLOOK_JP_CHAR.test(word)) : rawSuggestions;
        // 自動補正オフ: USF の補正後クエリ data.query は使わず、サジェスト語・コレクション候補のみ利用する。
        return { suggestions, collections };
      });
  }

  // ---- 描画（XSS セーフ: textContent / createElement のみ） ----------------

  clearResults() {
    if (this.suggestsEl) this.suggestsEl.replaceChildren();
    if (this.productsEl) {
      this.productsEl.replaceChildren();
      // 検索語が短くなった/中断した場合も含め、ロード表示を確実に消す。
      this.productsEl.classList.remove('is-loading');
    }
  }

  /**
   * サジェスト枠（商品より上）。並びは
   *   1) タレント別コレクションへの直行カード（最上段）
   *   2) サジェスト語のリスト
   * タレント以外のコレクション候補は直行カードにも一覧にも出さない。
   */
  renderSuggests(query, source) {
    if (!this.suggestsEl) return;
    const frag = document.createDocumentFragment();

    // 直行カードはタレント専用導線。USF のコレクション候補をテーマ出力のタレント辞書で絞り込み、
    // タレントコレクションが 1 件に確定したときだけ出す。タレント以外しか該当しないとき（誤直行になる）と
    // タレントが複数該当するとき（どれを出すか決められない）は出さない。
    const talentDict = QuicklookSearch.getTalentDictByHandle();
    const talentCollections = (source.collections || []).filter((c) => talentDict[c.handle]);
    const directCollection = talentCollections.length === 1 ? talentCollections[0] : null;

    if (directCollection) {
      // 表示名・画像・テーマ色は辞書のメタフィールド（name.japanese / name.english）を正とし、
      // 名前が空のときだけ USF のコレクションタイトルで補う。
      const meta = talentDict[directCollection.handle];
      const name =
        (this.locale === 'en' ? meta.name_en || meta.name_ja : meta.name_ja || meta.name_en) || directCollection.title;
      frag.appendChild(
        this.buildDirectCard({
          label: this.dataset.labelTalentDirect || '',
          title: this.talentDirectLabel.replace('{name}', name),
          url: this.collectionUrl(directCollection.handle),
          handle: directCollection.handle,
          source: 'talent',
          image: meta.image || '',
          themeColor: meta.theme_color || '',
        })
      );
    }

    // サジェスト語
    const suggestLimit = 3;
    const suggestions = (source.suggestions || []).slice(0, suggestLimit);
    if (suggestions.length) {
      if (this.dataset.labelSuggestions) {
        const head = document.createElement('div');
        head.className = 'QuicklookSearch_suggestHead';
        head.textContent = this.dataset.labelSuggestions;
        frag.appendChild(head);
      }
      const list = document.createElement('ul');
      list.className = 'QuicklookSearch_slist';
      suggestions.forEach((word, index) => {
        // ul の直下は li のみ許容されるため、各行の button を li で包む。
        const li = document.createElement('li');
        // index + 1 = サジェスト内の表示順位（GA4 click_position）。
        li.appendChild(this.buildSuggestRow(word, () => this.submitTerm(word, 'suggest', index + 1)));
        list.appendChild(li);
      });
      frag.appendChild(list);
    }

    this.suggestsEl.appendChild(frag);
  }

  /**
   * タレント直行カード（最上段の構造化導線）。label は空なら省略。
   * コレクション画像（image）があればアバター表示、テーマ色（themeColor）があれば
   * カードの背景色に反映する（コレクションのメタフィールド由来）。
   * 文字色は白が既定だが、背景が白に近いときだけ黒に切り替える。
   */
  buildDirectCard({ label, title, url, handle, source, image, themeColor }) {
    const card = document.createElement('a');
    card.className = 'QuicklookSearch_direct';
    card.href = url;
    const directStyle = themeColor ? quicklookDirectStyle(themeColor) : null;
    if (directStyle) {
      card.style.background = directStyle.background;
      // 白に近い背景のときだけ文字色を上書きする（それ以外は CSS 既定の白のまま）。
      if (directStyle.color) card.style.color = directStyle.color;
    }

    if (image) {
      const ava = document.createElement('span');
      ava.className = 'QuicklookSearch_directAva';
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      ava.appendChild(img);
      card.appendChild(ava);
    }

    const body = document.createElement('div');
    body.className = 'QuicklookSearch_directBody';
    if (label) {
      const labelEl = document.createElement('span');
      labelEl.className = 'QuicklookSearch_directLabel';
      labelEl.textContent = label;
      body.appendChild(labelEl);
    }
    const titleEl = document.createElement('span');
    titleEl.className = 'QuicklookSearch_directTitle';
    titleEl.textContent = title;
    body.appendChild(titleEl);

    const arrow = document.createElement('span');
    arrow.className = 'QuicklookSearch_directArrow';

    card.appendChild(body);
    card.appendChild(arrow);
    card.addEventListener('click', () =>
      document.dispatchEvent(
        new CustomEvent('quicklook:collection-click', {
          detail: { handle: handle || '', source: source || '', query: this.lastQuery },
        })
      )
    );
    return card;
  }

  /** サジェスト 1 行。クリックで onClick。 */
  buildSuggestRow(label, onClick) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'QuicklookSearch_sitem';

    // 虫眼鏡アイコンは CSS の mask で描く（QuicklookSearch_sitemIcon）。ここは空のマスク対象を置くだけ。
    const icon = document.createElement('span');
    icon.className = 'QuicklookSearch_sitemIcon';

    const text = document.createElement('span');
    text.className = 'QuicklookSearch_sitemText';
    text.textContent = label;

    item.appendChild(icon);
    item.appendChild(text);
    item.addEventListener('click', onClick);
    return item;
  }

  /**
   * 商品（サジェストの下に横スクロールで少数）＋「すべて見る」（フェイルオープン）。
   * productNodes は Section Rendering で取得した商品カード（.QuicklookSearch_card）のサーバー描画ノード配列。
   * failed=true（S&D 取得失敗）のときは商品行も「該当なし」も出さず「すべて見る」だけ描画する。
   * query はこの結果の検索語。失敗時は lastQuery が解除済みのため、this.lastQuery ではなく引数で受ける。
   * resultCount は S&D の総ヒット件数（表示上限 8 で間引く前）。「すべて見る」計測で送る。
   */
  renderProducts(productNodes, failed = false, query = '', resultCount = 0) {
    if (!this.productsEl) return;
    // 商品が確定したのでロード表示を解除し、実カードへ差し替える。
    this.productsEl.classList.remove('is-loading');

    const sec = document.createElement('div');
    sec.className = 'QuicklookSearch_prodsec';

    if (failed) {
      // S&D 取得失敗時は商品行も「該当なし」も出さない（在庫ありを 0 件と誤認させない）。
      // 下の「すべて見る」だけ残してネイティブ検索へ逃がす（フェイルオープン）。
    } else if (!productNodes.length) {
      const empty = document.createElement('p');
      empty.className = 'QuicklookSearch_empty';
      empty.textContent = this.dataset.labelNoResults || '';
      sec.appendChild(empty);
    } else {
      // リスト上部に「総ヒット件数中◯件表示」を出す。総件数（resultCount）が取れているときだけ表示し、
      // 取得失敗のフォールバック（resultCount=0）では誤った件数を出さない。
      if (this.dataset.labelResultCount && resultCount > 0) {
        const count = document.createElement('p');
        count.className = 'QuicklookSearch_count';
        count.textContent = this.dataset.labelResultCount
          .replace('{total}', resultCount)
          .replace('{shown}', productNodes.length);
        sec.appendChild(count);
      }

      const row = document.createElement('div');
      row.className = 'QuicklookSearch_prodrow';
      productNodes.forEach((card, index) => {
        // collection-item のカード（.Item）は href を内側の <a class="Item_inner"> に持つ。
        const cardLink = card.querySelector('a[href]');
        const href = cardLink ? cardLink.getAttribute('href') : '';
        card.addEventListener('click', () =>
          document.dispatchEvent(
            new CustomEvent('quicklook:product-click', {
              detail: {
                url: href,
                query,
                handle: quicklookProductHandleFromUrl(href),
                position: index + 1,
              },
            })
          )
        );
        row.appendChild(card);
      });
      sec.appendChild(row);
    }

    // 「『◯◯』の検索結果をすべて見る」導線。実ヒット 0 件のときは遷移先も 0 件なので出さない。
    // failed（S&D 取得失敗）は 0 件ではないためフェイルオープンで残し、ネイティブ検索へ逃がす。
    if (failed || productNodes.length) {
      const seeAll = document.createElement('a');
      seeAll.className = 'QuicklookSearch_seeAll';
      seeAll.href = this.searchResultsUrl(query);
      seeAll.textContent = (this.dataset.labelSeeAll || '{q}').replace('{q}', query);
      seeAll.addEventListener('click', () => {
        // <a> 遷移はフォーム送信を通らないため、ここで明示的に履歴保存する。
        this.saveSearchHistory(query);
        document.dispatchEvent(
          new CustomEvent('quicklook:see-all-click', {
            // 表示数（上限 8）ではなく総ヒット件数を送り、search_submitted と同じ result_count に揃える。
            detail: { query, resultCount },
          })
        );
      });
      sec.appendChild(seeAll);
    }

    this.productsEl.appendChild(sec);
  }

  // ---- ユーティリティ ----------------------------------------------------

  /** ロケール/マーケット対応の collection URL（ハードコード禁止）。 */
  collectionUrl(handle) {
    return `${quicklookShopifyRoot()}collections/${handle}`;
  }

  /** ロケール/マーケット対応の検索結果ページ URL。 */
  searchResultsUrl(term) {
    return `${quicklookShopifyRoot()}search?q=${encodeURIComponent(term)}&type=product`;
  }

  /** サジェスト語クリック等で、その語をネイティブのフォーム送信に乗せて検索する。 */
  submitTerm(term, source, position) {
    document.dispatchEvent(
      new CustomEvent('quicklook:suggest-click', {
        detail: { term, source, query: this.lastQuery, position: position || null },
      })
    );
    // form.submit() は submit イベントを発火しないため、ここで明示的に履歴保存する。
    this.saveSearchHistory(term);
    if (this.input) this.input.value = term;
    if (this.form) {
      this.form.submit();
    } else {
      window.location.href = this.searchResultsUrl(term);
    }
  }

  /** テーマが <script type="application/json" id="QuicklookTalentDict"> で出力した辞書を 1 度だけ読む。 */
  static getTalentDictionary() {
    if (QuicklookSearch.talentDictionary) return QuicklookSearch.talentDictionary;
    const el = document.getElementById('QuicklookTalentDict');
    if (!el) {
      QuicklookSearch.talentDictionary = [];
      return QuicklookSearch.talentDictionary;
    }
    try {
      QuicklookSearch.talentDictionary = JSON.parse(el.textContent) || [];
    } catch (e) {
      QuicklookSearch.talentDictionary = [];
    }
    return QuicklookSearch.talentDictionary;
  }

  /** タレント辞書を handle 引きできるよう 1 度だけマップ化する（直行カードの画像 / 色引き当て用）。 */
  static getTalentDictByHandle() {
    if (QuicklookSearch.talentDictByHandle) return QuicklookSearch.talentDictByHandle;
    const map = {};
    QuicklookSearch.getTalentDictionary().forEach((t) => {
      if (t.handle) map[t.handle] = t;
    });
    QuicklookSearch.talentDictByHandle = map;
    return map;
  }
}

QuicklookSearch.talentDictionary = null;
QuicklookSearch.talentDictByHandle = null;
customElements.define('quicklook-search', QuicklookSearch);

// ---- 閲覧履歴（最近見た商品）の描画 ---------------------------------------
// localStorage 'recentlyViewedProduct' の「最近見た商品」をヘッダー入力前パネル
// (#prtcRecentlyViewedProducts) に描画する。パネルを Section Rendering でオンデマンド挿入すると
// 挿入した <script> は実行されないため、描画ロジックを外部 JS に集約している。

/** localStorage から閲覧履歴配列を読む。未保存/壊れていれば null。 */
function quicklookLoadRecentlyViewed() {
  try {
    const data = JSON.parse(localStorage.getItem(QUICKLOOK_RECENT_KEY));
    return Array.isArray(data) ? data : null;
  } catch (e) {
    return null;
  }
}

/**
 * 閲覧履歴 1 件のカード要素（入力前パネル用・画像のみ）を組み立てる（XSS セーフ: createElement / textContent のみ）。
 * 画像は英語表示なら productImgEn を優先し、無ければ productImg（日本語）へフォールバックする。
 */
function quicklookBuildRecentItem(item, placeholder, isEn) {
  const wrap = document.createElement('div');
  wrap.className = 'Item';
  const link = document.createElement('a');
  link.className = 'Item_inner';
  link.href = item.productUrl || '#';
  const img = document.createElement('img');
  img.src = (isEn && item.productImgEn) || item.productImg || placeholder;
  // 画像のみのカードはリンク名が alt だけになるため商品名を入れる。
  img.alt = item.productTitle || '';
  link.appendChild(img);
  wrap.appendChild(link);
  return wrap;
}

/** 閲覧履歴（新しい順・最大 QUICKLOOK_RECENT_MAX 件）のカード群を DocumentFragment で返す。 */
function quicklookBuildRecentFragment(data, placeholder, isEn) {
  const frag = document.createDocumentFragment();
  data
    .slice(-QUICKLOOK_RECENT_MAX)
    .reverse()
    .forEach((item) => frag.appendChild(quicklookBuildRecentItem(item, placeholder, isEn)));
  return frag;
}

/**
 * ヘッダー入力前パネルの閲覧履歴 (#prtcRecentlyViewedProducts) を描画し、空ならセクション（見出し含む）ごと
 * 非表示にする。パネルを Section Rendering で挿入した直後に呼ぶ。
 */
function quicklookRenderRecentPanel() {
  const container = document.getElementById('prtcRecentlyViewedProducts');
  const data = container ? quicklookLoadRecentlyViewed() : null;
  if (container && data) {
    const instance = container.closest('quicklook-search');
    const locale = (instance && instance.dataset.locale) || (document.documentElement.lang || '').toLowerCase();
    const isEn = locale.slice(0, 2) === 'en';
    container.replaceChildren(quicklookBuildRecentFragment(data, container.dataset.noImage || '', isEn));
  }
  document.querySelectorAll('.QuicklookSearch_sec.recent_view').forEach((sec) => {
    const wrap = sec.querySelector('#prtcRecentlyViewedProducts, .recent_view_wrap');
    sec.hidden = !wrap || wrap.children.length === 0;
  });
}

// ---- 最近チェックしたタレント（タレント訪問履歴）の描画 -----------------------
// データ層（recent-talents.js）が localStorage 'recentlyViewedTalent' へ記録済みの履歴を
// window.quicklookRecentTalents.load() で読み、入力前パネルの [data-recent-talents] に丸型アバターで描画する。
// 記録（write）と読み出し（read）はデータ層の責務。ここは表示層＝DOM 生成のみを担う。

/** データ層からタレント履歴配列を読む。データ層が未読込なら []（フェイルセーフ）。 */
function quicklookLoadRecentTalents() {
  return (window.quicklookRecentTalents && window.quicklookRecentTalents.load()) || [];
}

/**
 * Shopify CDN の画像 URL の width クエリを表示サイズに合わせて上書きする。
 * 保存済み URL には別の width が付いていることがあるため、連結ではなく set で置き換える。
 * URL として解釈できない値はそのまま返す（フェイルセーフ）。
 */
function quicklookImageWithWidth(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (e) {
    return src;
  }
}

/**
 * タレント履歴 1 件の丸型アバターカードを組み立てる（XSS セーフ: createElement / textContent のみ）。
 * 注目の新作タレント（_quicklook-featured-talents）と同じ .QuicklookSearch_talent マークアップ（バッジ無し）。
 * href は表示ロケールに追従させるため handle から root 基点で生成し、handle が無ければ保存 url にフォールバック。
 */
function quicklookBuildTalentItem(item, isEn) {
  const link = document.createElement('a');
  link.className = 'QuicklookSearch_talent';
  link.href = item.handle ? `${quicklookShopifyRoot()}collections/${item.handle}` : item.url || '#';

  const ava = document.createElement('span');
  ava.className = 'QuicklookSearch_talentAva';
  if (item.image) {
    const img = document.createElement('img');
    // 64px 表示 × Retina(2x) = 128。注目の新作タレント（_quicklook-featured-talents）と同じ解像度に揃える。
    img.src = quicklookImageWithWidth(item.image, 128);
    img.alt = '';
    img.loading = 'lazy';
    img.width = 64;
    img.height = 64;
    ava.appendChild(img);
  }
  link.appendChild(ava);

  const name = document.createElement('span');
  name.className = 'QuicklookSearch_talentName';
  // 表示名は locale で振り分け、未設定側は他方へフォールバック（保存データで完結・辞書引き不要）。
  name.textContent = (isEn ? item.name_en : item.name_ja) || item.name_ja || item.name_en || '';
  link.appendChild(name);
  return link;
}

/** タレント履歴（新しい順・保存済み最大件）のカード群を DocumentFragment で返す。 */
function quicklookBuildTalentFragment(data, isEn) {
  const frag = document.createDocumentFragment();
  data.forEach((item) => frag.appendChild(quicklookBuildTalentItem(item, isEn)));
  return frag;
}

/**
 * 入力前パネルの「最近チェックしたタレント」([data-recent-talents]) を描画し、履歴が空ならセクションごと非表示にする。
 * PC/SP の2インスタンス双方を対象にする。パネル挿入直後に呼ぶ。
 */
function quicklookRenderRecentTalents() {
  const data = quicklookLoadRecentTalents();
  document.querySelectorAll('[data-recent-talents]').forEach((container) => {
    const instance = container.closest('quicklook-search');
    const locale = (instance && instance.dataset.locale) || (document.documentElement.lang || '').toLowerCase();
    const isEn = locale.slice(0, 2) === 'en';
    if (data.length > 0) {
      container.replaceChildren(quicklookBuildTalentFragment(data, isEn));
    }
    const section = container.closest('.QuicklookSearch_recentTalents');
    if (section) section.hidden = data.length === 0;
  });
}

// ---- パネルのオンデマンド取得（Section Rendering API） ----------------------
// 検索を開いた時に 1 度だけ .sp_search へパネル本体を挿入する。多重取得は状態と Promise の保持で防ぐ。
// 'idle'（未取得） / 'loading'（取得中） / 'loaded'（挿入済み）。
let quicklookPanelState = 'idle';
let quicklookPanelPromise = null;

/**
 * .sp_search にパネル本体（header-search-panel セクション）を挿入する。取得済みなら即解決。
 * fetch → DOMParser → importNode で light DOM に挿入し、connectedCallback と閲覧履歴描画を発火させる。
 * @returns {Promise<boolean>} 挿入済み/成功なら true、コンテナ無し・取得失敗なら false（フェイルオープン）。
 */
function ensureQuicklookPanel() {
  if (quicklookPanelState === 'loaded') return Promise.resolve(true);
  if (quicklookPanelPromise) return quicklookPanelPromise;

  const container = document.querySelector('.sp_search');
  if (!container) return Promise.resolve(false);
  // サーバー側で既に描画済み（フォールバック）なら取得しない。
  if (container.querySelector('quicklook-search')) {
    quicklookPanelState = 'loaded';
    return Promise.resolve(true);
  }

  quicklookPanelState = 'loading';
  const url = `${quicklookShopifyRoot()}?section_id=${QUICKLOOK_PANEL_SECTION}`;
  quicklookPanelPromise = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`panel ${res.status}`);
      return res.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      // Section Rendering のラッパー（<div id="shopify-section-...">）は挿入せず中身だけ .sp_search 直下へ移し、
      // サーバー描画時と同じ DOM 構造にする。
      const wrapper = doc.getElementById(`shopify-section-${QUICKLOOK_PANEL_SECTION}`);
      const sourceNodes = wrapper ? Array.from(wrapper.childNodes) : Array.from(doc.body.childNodes);
      sourceNodes.forEach((node) => container.appendChild(document.importNode(node, true)));

      quicklookPanelState = 'loaded';
      // 挿入で connectedCallback が発火し入力欄が bind 済み。入力前パネルの閲覧履歴・タレント履歴を描画する。
      quicklookRenderRecentPanel();
      quicklookRenderRecentTalents();
      return true;
    })
    .catch(() => {
      // フェイルオープン: パネルを出さない（各フォームのネイティブ /search 送信で検索は継続可能）。
      quicklookPanelState = 'idle';
      quicklookPanelPromise = null;
      return false;
    });
  return quicklookPanelPromise;
}

/**
 * 外部（ヘッダーの検索アイコン等）から QuickLook を開くための共通 opener。
 * SP は .sp_search を表示＋スクロールロックし、SP モードを起動する（フォーカスはせず＝ソフトキーボードを
 * 立ち上げない。開いた瞬間は入力前パネルで、結果の復元は入力欄フォーカス時に行う）。未取得ならパネル取得を待ってから開く。
 * PC は検索バー（.SearchForm）の入力欄へフォーカスし、focus を起点に配線がドロップダウンを開く。
 */
window.openQuicklookSearch = function (device, trigger) {
  if (device !== 'sp') {
    // PC: 入力欄フォーカスで wireQuicklookPcTrigger の focus ハンドラが ensure + activatePc を担う。
    const pcInput = document.querySelector('.Header_search.pc input[name="q"]');
    if (pcInput) pcInput.focus();
    return;
  }
  const openSp = () => {
    const panel = document.querySelector('.sp_search');
    if (!panel) return;
    // 既に開いている状態での再呼び出しでは open を二重計測しない。
    const wasOpen = panel.classList.contains('show');
    panel.classList.add('show');
    quicklookLockBodyForSp();
    // フォーカスはせず SP モードだけ起動する（開いた瞬間にキーボードを出さず、結果も復元しない＝入力前パネル）。
    const instance = panel.querySelector('quicklook-search');
    if (instance && typeof instance.activateSp === 'function') instance.activateSp();
    if (!wasOpen) {
      document.dispatchEvent(
        new CustomEvent('quicklook:open', { detail: { trigger: trigger || 'icon' } })
      );
    }
  };
  if (quicklookPanelState === 'loaded') {
    openSp();
    return;
  }
  // 未取得時はパネル取得を待ってから開く（フォーカスしないためキーボード保持の一時 input は不要）。
  ensureQuicklookPanel().then((ok) => {
    if (ok) openSp();
  });
};

// PC: ヘッダー検索バー（.Header_search.pc .SearchForm）の入力欄フォーカスで、パネルをオンデマンド取得して
// から単一インスタンスをドロップダウンとして開く。PC では入力前パネルの重い Liquid を二重描画しない。
function wireQuicklookPcTrigger() {
  const trigger = document.querySelector('.Header_search.pc .SearchForm');
  if (!trigger) return;
  const triggerInput = trigger.querySelector('input[name="q"]');
  if (!triggerInput) return;
  triggerInput.addEventListener('focus', () => {
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    ensureQuicklookPanel().then((ok) => {
      if (!ok) return;
      const instance = document.querySelector('.sp_search quicklook-search');
      if (instance && typeof instance.activatePc === 'function') instance.activatePc(trigger);
    });
  });
}

// パネル本体をアイドル時に先読みする。検索に触れる頃には取得済みで focus/tap は即開き、SP の同期 focus で
// ソフトキーボードも開く。ensureQuicklookPanel は冪等。省データモード（Save-Data）では先読みしない。
function scheduleQuicklookPanelPrefetch() {
  const conn = navigator.connection;
  if (conn && conn.saveData) return;
  const prefetch = () => ensureQuicklookPanel();
  if ('requestIdleCallback' in window) {
    // timeout: アイドルが来なくても最大 3s で必ず先読みする backstop。
    requestIdleCallback(prefetch, { timeout: 3000 });
  } else {
    setTimeout(prefetch, 1200);
  }
}

// ヘッダーの検索アイコン（.HeaderSp_search / .Header_spSearch）から QuickLook を開く。
document.addEventListener('DOMContentLoaded', () => {
  wireQuicklookPcTrigger();
  scheduleQuicklookPanelPrefetch();

  ['.HeaderSp_search', '.Header_spSearch'].forEach((selector) => {
    const trigger = document.querySelector(selector);
    if (!trigger) return;
    trigger.addEventListener('click', () => {
      const isPc = window.matchMedia('(min-width: 768px)').matches;
      window.openQuicklookSearch(isPc ? 'pc' : 'sp', 'icon');
    });
  });
});
